import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  STOCK_LOCATION_KIND_LABELS,
  formatCents,
  type StockLocation,
  type StockLocationKind,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ActiveBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

/**
 * Locais de estoque: almoxarifado central, frentes de obra e material em poder
 * de terceiro. Separar por local é o que permite responder "cadê o cimento" —
 * um saldo único da empresa não serve para nada num canteiro.
 */
export function LocaisPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StockLocation | null>(null);
  const [removing, setRemoving] = useState<StockLocation | null>(null);

  const listQuery = useQuery({
    queryKey: ['stock-locations'],
    queryFn: () => api.get<{ data: StockLocation[] }>('/stock-locations'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['stock-locations'] });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/stock-locations/${id}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const rows = listQuery.data?.data ?? [];
  const totalCents = rows.reduce((acc, row) => acc + row.totalValueCents, 0);

  const columns: Array<Column<StockLocation>> = [
    { key: 'code', label: 'Código', mono: true, strong: true, render: (row) => row.code },
    { key: 'name', label: 'Local', render: (row) => row.name },
    { key: 'kind', label: 'Tipo', render: (row) => STOCK_LOCATION_KIND_LABELS[row.kind] },
    {
      key: 'itemCount',
      label: 'Itens com saldo',
      right: true,
      mono: true,
      render: (row) => row.itemCount.toLocaleString('pt-BR'),
    },
    {
      key: 'totalValueCents',
      label: 'Valor estocado',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.totalValueCents),
    },
    { key: 'isActive', label: 'Situação', render: (row) => <ActiveBadge active={row.isActive} /> },
    {
      key: 'actions',
      label: '',
      width: 96,
      right: true,
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {can('locais-estoque:editar') && (
            <Button small variant="ghost" icon={Pencil} aria-label={`Editar ${row.name}`} onClick={() => setEditing(row)} />
          )}
          {can('locais-estoque:excluir') && (
            <Button small variant="ghost" icon={Trash2} aria-label={`Excluir ${row.name}`} onClick={() => setRemoving(row)} />
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Locais de estoque"
        meta={[
          { label: 'Valor estocado', value: formatCents(totalCents) },
        ]}
        actions={
          can('locais-estoque:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Novo local
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {listQuery.isPending && <TableSkeleton columns={7} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Locais de estoque"
            columns={columns}
            rows={rows}
            emptyState={
              <EmptyState
                title="Nenhum local cadastrado"
                description="Cadastre o almoxarifado central antes da primeira entrada de material."
                action={
                  can('locais-estoque:criar') ? (
                    <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                      Novo local
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}
      </div>

      <LocationForm
        open={creating || Boolean(editing)}
        location={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          refresh();
        }}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        destructive
        loading={removeMutation.isPending}
        title="Excluir este local?"
        confirmLabel="Excluir"
        message={
          <>
            Um local com saldo não pode ser excluído — transfira o material
            antes. O histórico de movimentações permanece de qualquer forma.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(null);
          removeMutation.reset();
        }}
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </>
  );
}

function LocationForm({
  open,
  location,
  onClose,
  onSaved,
}: {
  open: boolean;
  location: StockLocation | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<StockLocationKind>('ALMOXARIFADO');
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (location?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setCode(location?.code ?? '');
    setName(location?.name ?? '');
    setKind(location?.kind ?? 'ALMOXARIFADO');
    setIsActive(location?.isActive ?? true);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      location ? api.patch(`/stock-locations/${location.id}`, body) : api.post('/stock-locations', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={location ? 'Editar local' : 'Novo local'}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={code.trim() === '' || name.trim().length < 2}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                code: code.trim().toUpperCase(),
                name: name.trim(),
                kind,
                isActive,
              })
            }
          >
            {location ? 'Salvar' : 'Criar'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <div className="grid-2">
          <Field label="Código" htmlFor="loc-code" error={errors.code}>
            <input
              id="loc-code"
              className="input input--mono"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="ALM-01"
            />
          </Field>
          <Field label="Tipo" htmlFor="loc-kind">
            <select
              id="loc-kind"
              className="input"
              value={kind}
              onChange={(event) => setKind(event.target.value as StockLocationKind)}
            >
              {Object.entries(STOCK_LOCATION_KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Nome" htmlFor="loc-name" error={errors.name}>
          <input
            id="loc-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Almoxarifado central"
          />
        </Field>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          Local ativo
        </label>
      </FormSection>
    </Modal>
  );
}
